import { Helmet } from 'react-helmet-async';

import { MEDIA } from '../../constants/media';
import {
  getCanonicalUrl,
  getRouteSeo,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_WIDTH,
  SITE_LOCALE,
  SITE_NAME,
  type RouteSeoKey,
} from '../../constants/seo';

type SeoHeadProps = {
  route: RouteSeoKey;
  /** Override canonical path (e.g. `/s/abc123` still uses `/s` robots config) */
  canonicalPath?: string;
  /** Override page title (document, og:title, twitter:title) */
  title?: string;
};

export default function SeoHead({ route, canonicalPath, title }: SeoHeadProps) {
  const seo = getRouteSeo(route);
  const path = canonicalPath ?? seo.path;
  const canonicalUrl = getCanonicalUrl(path);
  const ogType = seo.ogType ?? 'website';
  const pageTitle = title ?? seo.title;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={seo.description} />
      <link rel="canonical" href={canonicalUrl} />

      {seo.robots && <meta name="robots" content={seo.robots} />}

      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={MEDIA.thumbnail} />
      <meta property="og:image:type" content="image/webp" />
      <meta property="og:image:width" content={String(OG_IMAGE_WIDTH)} />
      <meta property="og:image:height" content={String(OG_IMAGE_HEIGHT)} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={SITE_LOCALE} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={MEDIA.thumbnail} />
    </Helmet>
  );
}
